import React, { useEffect, useMemo, useState } from 'react';

import {
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    InputLabel,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

import { PopupTransition } from 'components/@extended/Transitions';
import IncidentReportService from 'services/incident-report.service';
import SnackbarNotify from 'utils/snackbar/SnackbarNotify';
import { firestoreTimestampToDate } from 'utils/firestore-timestamp';
import {
    INCIDENT_CATEGORIES,
    INCIDENT_CATEGORY_SUBTYPES,
    INCIDENT_PARTY_ROLES,
    INCIDENT_PARTY_SOURCES,
    INCIDENT_SEVERITIES,
    INCIDENT_TIME_OBSERVED,
    INCIDENT_VEHICLE_SOURCES,
    errorMessageFrom,
    humanize
} from 'utils/helpers/guard-report.helper';

// ==============================|| GUARD REPORTS - SUPERVISOR EDIT ||============================== //

const emptyForm = {
    category: '',
    sub_type: '',
    sub_type_other: '',
    severity: '',
    occurred_at: null,
    time_observed: '',
    location_detail: '',
    narrative: { observed: '', actions: '', outcome: '' },
    parties: [],
    vehicles: [],
    witnesses: []
};

const formFromReport = report => ({
    category: report?.category || '',
    sub_type: report?.sub_type || '',
    sub_type_other: report?.sub_type_other || '',
    severity: report?.severity || '',
    occurred_at: firestoreTimestampToDate(report?.occurred_at),
    time_observed: report?.time_observed || '',
    location_detail: report?.location_detail || '',
    narrative: {
        observed: report?.narrative?.observed || '',
        actions: report?.narrative?.actions || '',
        outcome: report?.narrative?.outcome || ''
    },
    // Spread rather than pick specific keys — an existing entry's `user_id` /
    // `lpr_record_id` rides along untouched and is never rendered as an editable
    // field (see the party/vehicle row markup below).
    parties: (report?.parties || []).map(p => ({ ...p })),
    vehicles: (report?.vehicles || []).map(v => ({ ...v })),
    witnesses: (report?.witnesses || []).map(w => ({ ...w }))
});

// New rows deliberately have no `user_id` / `lpr_record_id` key at all — the web
// must not invent account or LPR links, only preserve ones the guard already made.
const emptyParty = () => ({ name: '', role: '', source: '', description: '', last_seen: '' });
const emptyVehicle = () => ({ plate: '', description: '', source: '', owner_name: '', owner_type: '' });
const emptyWitness = () => ({ name: '', phone: '', statement: '' });

const isPartyComplete = p => !!(p.name?.trim() && p.role && p.source);
const isVehicleComplete = v => !!(v.plate?.trim() && v.description?.trim() && v.source);
const isWitnessComplete = w => !!w.name?.trim();

// Trims and drops empty-string optional fields to an omitted key rather than `''`
// or `null`, so an untouched blank field never appears in the PATCH body. Both
// sides of a `buildChanges` comparison go through the same normalizer, so two
// lists that only differ in incidental whitespace or key order compare equal.
const normalizeParty = p => {
    const out = { name: p.name.trim(), role: p.role, source: p.source };
    if (p.user_id != null) out.user_id = p.user_id;
    if (p.description?.trim()) out.description = p.description.trim();
    if (p.last_seen?.trim()) out.last_seen = p.last_seen.trim();
    return out;
};

const normalizeVehicle = v => {
    const out = { plate: v.plate.trim(), description: v.description.trim(), source: v.source };
    if (v.lpr_record_id != null) out.lpr_record_id = v.lpr_record_id;
    if (v.owner_name?.trim()) out.owner_name = v.owner_name.trim();
    if (v.owner_type?.trim()) out.owner_type = v.owner_type.trim();
    return out;
};

const normalizeWitness = w => {
    const out = { name: w.name.trim() };
    if (w.phone?.trim()) out.phone = w.phone.trim();
    if (w.statement?.trim()) out.statement = w.statement.trim();
    return out;
};

// PATCH replaces each list wholesale, so a changed entry means the WHOLE array
// goes in the request — never a per-item diff.
const listsDiffer = (formList, storedList, normalize) =>
    JSON.stringify(formList.map(normalize)) !== JSON.stringify((storedList || []).map(normalize));

// Only the fields the user actually changed go in the PATCH body — the API
// 400s an explicit null on most of these, so "unchanged" must mean "absent".
const buildChanges = (form, report) => {
    const changes = {};

    if (form.category !== report.category) changes.category = form.category;
    if (form.sub_type !== report.sub_type) changes.sub_type = form.sub_type;

    const newEndsOther = form.sub_type?.endsWith('_other');
    const oldEndsOther = report.sub_type?.endsWith('_other');
    if (newEndsOther) {
        const trimmed = form.sub_type_other?.trim() || '';
        if (trimmed !== (report.sub_type_other || '')) changes.sub_type_other = trimmed;
    } else if (oldEndsOther) {
        // Switched away from an `_other` sub-type — a leftover value 400s.
        changes.sub_type_other = null;
    }

    if (form.severity !== report.severity) changes.severity = form.severity;

    const originalOccurredAt = firestoreTimestampToDate(report.occurred_at);
    if (
        form.occurred_at &&
        (!originalOccurredAt || form.occurred_at.getTime() !== originalOccurredAt.getTime())
    ) {
        changes.occurred_at = form.occurred_at.toISOString();
    }

    if (form.time_observed !== report.time_observed) changes.time_observed = form.time_observed;

    const trimmedLocationDetail = form.location_detail?.trim() || '';
    if (trimmedLocationDetail !== (report.location_detail || '')) {
        changes.location_detail = trimmedLocationDetail || null;
    }

    const original = report.narrative || {};
    const narrativeChanged =
        form.narrative.observed.trim() !== (original.observed || '') ||
        form.narrative.actions.trim() !== (original.actions || '') ||
        (form.narrative.outcome?.trim() || '') !== (original.outcome || '');
    if (narrativeChanged) {
        changes.narrative = {
            observed: form.narrative.observed.trim(),
            actions: form.narrative.actions.trim(),
            outcome: form.narrative.outcome?.trim() || null
        };
    }

    if (listsDiffer(form.parties, report.parties, normalizeParty)) {
        changes.parties = form.parties.map(normalizeParty);
    }
    if (listsDiffer(form.vehicles, report.vehicles, normalizeVehicle)) {
        changes.vehicles = form.vehicles.map(normalizeVehicle);
    }
    if (listsDiffer(form.witnesses, report.witnesses, normalizeWitness)) {
        changes.witnesses = form.witnesses.map(normalizeWitness);
    }

    return changes;
};

const IncidentReportEditDialog = ({ open, report, orgId, onClose, onSaved }) => {
    const [form, setForm] = useState(emptyForm);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) setForm(formFromReport(report));
        // Re-seed only when the dialog opens (or opens for a different report) —
        // not on every keystroke.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, report?.id]);

    const categoryOptions = useMemo(
        () => INCIDENT_CATEGORIES.map(value => ({ label: humanize(value), value })),
        []
    );
    const subTypeOptions = useMemo(
        () =>
            (INCIDENT_CATEGORY_SUBTYPES[form.category] || []).map(value => ({
                label: humanize(value),
                value
            })),
        [form.category]
    );
    const severityOptions = useMemo(
        () => INCIDENT_SEVERITIES.map(value => ({ label: humanize(value), value })),
        []
    );
    const timeObservedOptions = useMemo(
        () => INCIDENT_TIME_OBSERVED.map(value => ({ label: humanize(value), value })),
        []
    );
    const partyRoleOptions = useMemo(
        () => INCIDENT_PARTY_ROLES.map(value => ({ label: humanize(value), value })),
        []
    );
    const partySourceOptions = useMemo(
        () => INCIDENT_PARTY_SOURCES.map(value => ({ label: humanize(value), value })),
        []
    );
    const vehicleSourceOptions = useMemo(
        () => INCIDENT_VEHICLE_SOURCES.map(value => ({ label: humanize(value), value })),
        []
    );

    const isOther = form.sub_type?.endsWith('_other');
    const isValid =
        form.category &&
        form.sub_type &&
        form.severity &&
        form.time_observed &&
        form.occurred_at &&
        form.narrative.observed.trim() &&
        form.narrative.actions.trim() &&
        // The API requires at least 2 non-space characters for an `_other` sub-type.
        (!isOther || (form.sub_type_other?.trim().length ?? 0) >= 2) &&
        form.parties.every(isPartyComplete) &&
        form.vehicles.every(isVehicleComplete) &&
        form.witnesses.every(isWitnessComplete);

    const addListItem = (key, factory) => {
        setForm(prev => ({ ...prev, [key]: [...prev[key], factory()] }));
    };

    const updateListItem = (key, index, patch) => {
        setForm(prev => {
            const list = [...prev[key]];
            list[index] = { ...list[index], ...patch };
            return { ...prev, [key]: list };
        });
    };

    const removeListItem = (key, index) => {
        setForm(prev => ({ ...prev, [key]: prev[key].filter((_, i) => i !== index) }));
    };

    const handleCategoryChange = value => {
        const validSubTypes = INCIDENT_CATEGORY_SUBTYPES[value] || [];
        setForm(prev => {
            const keepSubType = validSubTypes.includes(prev.sub_type);
            return {
                ...prev,
                category: value || '',
                sub_type: keepSubType ? prev.sub_type : '',
                sub_type_other: keepSubType ? prev.sub_type_other : ''
            };
        });
    };

    const handleSubTypeChange = value => {
        setForm(prev => ({
            ...prev,
            sub_type: value || '',
            sub_type_other: value?.endsWith('_other') ? prev.sub_type_other : ''
        }));
    };

    const handleSave = async () => {
        const changes = buildChanges(form, report);
        if (Object.keys(changes).length === 0) {
            onClose();
            return;
        }
        setSubmitting(true);
        try {
            const updated = await IncidentReportService.edit(orgId, report.id, changes);
            SnackbarNotify.success('Report updated.');
            onSaved(updated);
            onClose();
        } catch (e) {
            SnackbarNotify.error(`Failed to update report - ${errorMessageFrom(e)}`);
        } finally {
            setSubmitting(false);
        }
    };

    if (!report) return null;

    return (
        <Dialog
            maxWidth="md"
            fullWidth
            keepMounted
            TransitionComponent={PopupTransition}
            open={open}
            onClose={submitting ? undefined : onClose}
        >
            <DialogTitle>Edit report {report.report_no}</DialogTitle>
            <Divider />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DialogContent sx={{ p: 2.5 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Stack spacing={0.5}>
                                <InputLabel>Category</InputLabel>
                                <Autocomplete
                                    disableClearable
                                    options={categoryOptions}
                                    getOptionLabel={option => option.label || ''}
                                    isOptionEqualToValue={(option, value) =>
                                        option.value === value.value
                                    }
                                    value={
                                        categoryOptions.find(
                                            o => o.value === form.category
                                        ) || null
                                    }
                                    onChange={(_, value) =>
                                        handleCategoryChange(value?.value)
                                    }
                                    size="small"
                                    renderInput={params => (
                                        <TextField {...params} size="small" />
                                    )}
                                />
                            </Stack>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Stack spacing={0.5}>
                                <InputLabel>Sub-type</InputLabel>
                                <Autocomplete
                                    disableClearable
                                    disabled={!form.category}
                                    options={subTypeOptions}
                                    getOptionLabel={option => option.label || ''}
                                    isOptionEqualToValue={(option, value) =>
                                        option.value === value.value
                                    }
                                    value={
                                        subTypeOptions.find(
                                            o => o.value === form.sub_type
                                        ) || null
                                    }
                                    onChange={(_, value) =>
                                        handleSubTypeChange(value?.value)
                                    }
                                    size="small"
                                    renderInput={params => (
                                        <TextField {...params} size="small" />
                                    )}
                                />
                            </Stack>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Stack spacing={0.5}>
                                <InputLabel>Sub-type detail</InputLabel>
                                <TextField
                                    size="small"
                                    fullWidth
                                    disabled={!isOther}
                                    placeholder={
                                        isOther ? 'Describe the sub-type' : 'Not applicable'
                                    }
                                    value={form.sub_type_other}
                                    onChange={e =>
                                        setForm(prev => ({
                                            ...prev,
                                            sub_type_other: e.target.value
                                        }))
                                    }
                                />
                            </Stack>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Stack spacing={0.5}>
                                <InputLabel>Severity</InputLabel>
                                <Autocomplete
                                    disableClearable
                                    options={severityOptions}
                                    getOptionLabel={option => option.label || ''}
                                    isOptionEqualToValue={(option, value) =>
                                        option.value === value.value
                                    }
                                    value={
                                        severityOptions.find(
                                            o => o.value === form.severity
                                        ) || null
                                    }
                                    onChange={(_, value) =>
                                        setForm(prev => ({
                                            ...prev,
                                            severity: value?.value || ''
                                        }))
                                    }
                                    size="small"
                                    renderInput={params => (
                                        <TextField {...params} size="small" />
                                    )}
                                />
                            </Stack>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Stack spacing={0.5}>
                                <InputLabel>Time observed</InputLabel>
                                <Autocomplete
                                    disableClearable
                                    options={timeObservedOptions}
                                    getOptionLabel={option => option.label || ''}
                                    isOptionEqualToValue={(option, value) =>
                                        option.value === value.value
                                    }
                                    value={
                                        timeObservedOptions.find(
                                            o => o.value === form.time_observed
                                        ) || null
                                    }
                                    onChange={(_, value) =>
                                        setForm(prev => ({
                                            ...prev,
                                            time_observed: value?.value || ''
                                        }))
                                    }
                                    size="small"
                                    renderInput={params => (
                                        <TextField {...params} size="small" />
                                    )}
                                />
                            </Stack>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Stack spacing={0.5}>
                                <InputLabel>Occurred at</InputLabel>
                                <DateTimePicker
                                    value={form.occurred_at}
                                    onChange={value =>
                                        setForm(prev => ({ ...prev, occurred_at: value }))
                                    }
                                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                />
                            </Stack>
                        </Grid>

                        <Grid item xs={12}>
                            <Stack spacing={0.5}>
                                <InputLabel>Location detail</InputLabel>
                                <TextField
                                    size="small"
                                    fullWidth
                                    placeholder="Optional"
                                    value={form.location_detail}
                                    onChange={e =>
                                        setForm(prev => ({
                                            ...prev,
                                            location_detail: e.target.value
                                        }))
                                    }
                                />
                            </Stack>
                        </Grid>

                        <Grid item xs={12}>
                            <Stack spacing={0.5}>
                                <InputLabel>Observed</InputLabel>
                                <TextField
                                    size="small"
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    value={form.narrative.observed}
                                    onChange={e =>
                                        setForm(prev => ({
                                            ...prev,
                                            narrative: {
                                                ...prev.narrative,
                                                observed: e.target.value
                                            }
                                        }))
                                    }
                                />
                            </Stack>
                        </Grid>

                        <Grid item xs={12}>
                            <Stack spacing={0.5}>
                                <InputLabel>Actions taken</InputLabel>
                                <TextField
                                    size="small"
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    value={form.narrative.actions}
                                    onChange={e =>
                                        setForm(prev => ({
                                            ...prev,
                                            narrative: {
                                                ...prev.narrative,
                                                actions: e.target.value
                                            }
                                        }))
                                    }
                                />
                            </Stack>
                        </Grid>

                        <Grid item xs={12}>
                            <Stack spacing={0.5}>
                                <InputLabel>Outcome</InputLabel>
                                <TextField
                                    size="small"
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    placeholder="Optional"
                                    value={form.narrative.outcome}
                                    onChange={e =>
                                        setForm(prev => ({
                                            ...prev,
                                            narrative: {
                                                ...prev.narrative,
                                                outcome: e.target.value
                                            }
                                        }))
                                    }
                                />
                            </Stack>
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{ mb: 1.5 }}
                            >
                                <Typography variant="subtitle1">Parties</Typography>
                                <Button
                                    size="small"
                                    startIcon={<PlusOutlined />}
                                    onClick={() => addListItem('parties', emptyParty)}
                                >
                                    Add party
                                </Button>
                            </Stack>
                            <Stack spacing={2}>
                                {form.parties.map((party, index) => (
                                    <Stack
                                        key={index}
                                        direction="row"
                                        spacing={1.5}
                                        useFlexGap
                                        alignItems="flex-start"
                                        sx={{ flexWrap: 'wrap', rowGap: 1.5 }}
                                    >
                                        <TextField
                                            size="small"
                                            label="Name"
                                            sx={{ flex: '1 1 160px' }}
                                            value={party.name}
                                            onChange={e =>
                                                updateListItem('parties', index, {
                                                    name: e.target.value
                                                })
                                            }
                                        />
                                        <Autocomplete
                                            disableClearable
                                            size="small"
                                            sx={{ flex: '1 1 150px' }}
                                            options={partyRoleOptions}
                                            getOptionLabel={option => option.label || ''}
                                            isOptionEqualToValue={(option, value) =>
                                                option.value === value.value
                                            }
                                            value={
                                                partyRoleOptions.find(
                                                    o => o.value === party.role
                                                ) || null
                                            }
                                            onChange={(_, value) =>
                                                updateListItem('parties', index, {
                                                    role: value?.value || ''
                                                })
                                            }
                                            renderInput={params => (
                                                <TextField {...params} label="Role" size="small" />
                                            )}
                                        />
                                        <Autocomplete
                                            disableClearable
                                            size="small"
                                            sx={{ flex: '1 1 150px' }}
                                            options={partySourceOptions}
                                            getOptionLabel={option => option.label || ''}
                                            isOptionEqualToValue={(option, value) =>
                                                option.value === value.value
                                            }
                                            value={
                                                partySourceOptions.find(
                                                    o => o.value === party.source
                                                ) || null
                                            }
                                            onChange={(_, value) =>
                                                updateListItem('parties', index, {
                                                    source: value?.value || ''
                                                })
                                            }
                                            renderInput={params => (
                                                <TextField {...params} label="Source" size="small" />
                                            )}
                                        />
                                        <TextField
                                            size="small"
                                            label="Description"
                                            placeholder="Optional"
                                            sx={{ flex: '1 1 160px' }}
                                            value={party.description || ''}
                                            onChange={e =>
                                                updateListItem('parties', index, {
                                                    description: e.target.value
                                                })
                                            }
                                        />
                                        <TextField
                                            size="small"
                                            label="Last seen"
                                            placeholder="Optional"
                                            sx={{ flex: '1 1 140px' }}
                                            value={party.last_seen || ''}
                                            onChange={e =>
                                                updateListItem('parties', index, {
                                                    last_seen: e.target.value
                                                })
                                            }
                                        />
                                        <Tooltip title="Remove party">
                                            <Button
                                                color="error"
                                                onClick={() => removeListItem('parties', index)}
                                            >
                                                <DeleteOutlined />
                                            </Button>
                                        </Tooltip>
                                    </Stack>
                                ))}
                                {form.parties.length === 0 && (
                                    <Typography variant="body2" color="secondary">
                                        No parties recorded.
                                    </Typography>
                                )}
                            </Stack>
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{ mb: 1.5 }}
                            >
                                <Typography variant="subtitle1">Vehicles</Typography>
                                <Button
                                    size="small"
                                    startIcon={<PlusOutlined />}
                                    onClick={() => addListItem('vehicles', emptyVehicle)}
                                >
                                    Add vehicle
                                </Button>
                            </Stack>
                            <Stack spacing={2}>
                                {form.vehicles.map((vehicle, index) => (
                                    <Stack
                                        key={index}
                                        direction="row"
                                        spacing={1.5}
                                        useFlexGap
                                        alignItems="flex-start"
                                        sx={{ flexWrap: 'wrap', rowGap: 1.5 }}
                                    >
                                        <TextField
                                            size="small"
                                            label="Plate"
                                            sx={{ flex: '1 1 120px' }}
                                            value={vehicle.plate}
                                            onChange={e =>
                                                updateListItem('vehicles', index, {
                                                    plate: e.target.value
                                                })
                                            }
                                        />
                                        <TextField
                                            size="small"
                                            label="Description"
                                            sx={{ flex: '1 1 160px' }}
                                            value={vehicle.description}
                                            onChange={e =>
                                                updateListItem('vehicles', index, {
                                                    description: e.target.value
                                                })
                                            }
                                        />
                                        <Autocomplete
                                            disableClearable
                                            size="small"
                                            sx={{ flex: '1 1 150px' }}
                                            options={vehicleSourceOptions}
                                            getOptionLabel={option => option.label || ''}
                                            isOptionEqualToValue={(option, value) =>
                                                option.value === value.value
                                            }
                                            value={
                                                vehicleSourceOptions.find(
                                                    o => o.value === vehicle.source
                                                ) || null
                                            }
                                            onChange={(_, value) =>
                                                updateListItem('vehicles', index, {
                                                    source: value?.value || ''
                                                })
                                            }
                                            renderInput={params => (
                                                <TextField {...params} label="Source" size="small" />
                                            )}
                                        />
                                        <TextField
                                            size="small"
                                            label="Owner name"
                                            placeholder="Optional"
                                            sx={{ flex: '1 1 160px' }}
                                            value={vehicle.owner_name || ''}
                                            onChange={e =>
                                                updateListItem('vehicles', index, {
                                                    owner_name: e.target.value
                                                })
                                            }
                                        />
                                        <TextField
                                            size="small"
                                            label="Owner type"
                                            placeholder="Optional"
                                            sx={{ flex: '1 1 140px' }}
                                            value={vehicle.owner_type || ''}
                                            onChange={e =>
                                                updateListItem('vehicles', index, {
                                                    owner_type: e.target.value
                                                })
                                            }
                                        />
                                        <Tooltip title="Remove vehicle">
                                            <Button
                                                color="error"
                                                onClick={() => removeListItem('vehicles', index)}
                                            >
                                                <DeleteOutlined />
                                            </Button>
                                        </Tooltip>
                                    </Stack>
                                ))}
                                {form.vehicles.length === 0 && (
                                    <Typography variant="body2" color="secondary">
                                        No vehicles recorded.
                                    </Typography>
                                )}
                            </Stack>
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{ mb: 1.5 }}
                            >
                                <Typography variant="subtitle1">Witnesses</Typography>
                                <Button
                                    size="small"
                                    startIcon={<PlusOutlined />}
                                    onClick={() => addListItem('witnesses', emptyWitness)}
                                >
                                    Add witness
                                </Button>
                            </Stack>
                            <Stack spacing={2}>
                                {form.witnesses.map((witness, index) => (
                                    <Stack
                                        key={index}
                                        direction="row"
                                        spacing={1.5}
                                        useFlexGap
                                        alignItems="flex-start"
                                        sx={{ flexWrap: 'wrap', rowGap: 1.5 }}
                                    >
                                        <TextField
                                            size="small"
                                            label="Name"
                                            sx={{ flex: '1 1 160px' }}
                                            value={witness.name}
                                            onChange={e =>
                                                updateListItem('witnesses', index, {
                                                    name: e.target.value
                                                })
                                            }
                                        />
                                        <TextField
                                            size="small"
                                            label="Phone"
                                            placeholder="Optional"
                                            sx={{ flex: '1 1 140px' }}
                                            value={witness.phone || ''}
                                            onChange={e =>
                                                updateListItem('witnesses', index, {
                                                    phone: e.target.value
                                                })
                                            }
                                        />
                                        <TextField
                                            size="small"
                                            label="Statement"
                                            placeholder="Optional"
                                            sx={{ flex: '1 1 220px' }}
                                            value={witness.statement || ''}
                                            onChange={e =>
                                                updateListItem('witnesses', index, {
                                                    statement: e.target.value
                                                })
                                            }
                                        />
                                        <Tooltip title="Remove witness">
                                            <Button
                                                color="error"
                                                onClick={() => removeListItem('witnesses', index)}
                                            >
                                                <DeleteOutlined />
                                            </Button>
                                        </Tooltip>
                                    </Stack>
                                ))}
                                {form.witnesses.length === 0 && (
                                    <Typography variant="body2" color="secondary">
                                        No witnesses recorded.
                                    </Typography>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>
                </DialogContent>
            </LocalizationProvider>
            <Divider />
            <DialogActions sx={{ p: 2.5 }}>
                <Button onClick={onClose} disabled={submitting} color="secondary">
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    disabled={submitting || !isValid}
                    variant="contained"
                >
                    {submitting ? 'Saving…' : 'Save changes'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default IncidentReportEditDialog;
